import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  authLoading: boolean;
  login: (username: string, password: string) => boolean;
  logout: () => void;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  authLoading: true,
  login: () => false,
  logout: () => {},
});

export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [authLoading, setAuthLoading] = useState<boolean>(true);

  // Check if user is already authenticated on initial load
  useEffect(() => {
    setAuthLoading(true);
    const auth = localStorage.getItem('isAuthenticated');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
    // Add a small delay to ensure consistent loading experience
    const timer = setTimeout(() => {
      setAuthLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Login function with hardcoded credentials
  const login = (username: string, password: string): boolean => {
    // Hardcoded credentials as requested
    if (username === 'admin' && password === 'admin@123') {
      setIsAuthenticated(true);
      localStorage.setItem('isAuthenticated', 'true');
      return true;
    }
    return false;
  };

  // Logout function
  const logout = (): void => {
    setIsAuthenticated(false);
    localStorage.removeItem('isAuthenticated');

    // Also clear any redirect flags from localStorage
    localStorage.removeItem('redirectAfterAuth');
    localStorage.removeItem('redirectPath');
  };

  // Effect to sync state changes with localStorage
  useEffect(() => {
    if (isAuthenticated) {
      localStorage.setItem('isAuthenticated', 'true');
    } else {
      // Only remove if it exists and we're not authenticated
      if (localStorage.getItem('isAuthenticated')) {
        localStorage.removeItem('isAuthenticated');
      }
    }
  }, [isAuthenticated]);

  const value = {
    isAuthenticated,
    authLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
